import { CustomersController } from '@/shared/endpoints/customers';
import { IRefillConsent } from '@/shared/endpoints/customers/models/response';
import { IConvertedShipment, IDeliveryItem, IDeliveryPackage, IDeliveryTicket, IInvoice, IPendingPrescription } from '@/shared/types/interfaces';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { getInitalStateDefField, handleApiCases, IStateDefaultField } from '@/store/helpers';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isInvoiceValid } from '../../../shared/helpers/main';
import { ICustomerRequestItem, ICustomerRequestItems } from '@/shared/types/interfaces/customer-requests';
import { StateTrigger } from '@/shared/types/enums/prescriptionState';

const DELIVERY_EXCLUDED_MED_IDS = new Set(['19', '20']);
const DELIVERY_SUPPORTED_SUPPLY_TYPES = new Set(['MEDBOX', 'MEDBOX-LID']);
const NDCS_TO_HIDE = (import.meta.env.VITE_APP_NDCS_TO_HIDE as string ?? '').split(',').filter(Boolean);

const filterDeliveryItems = (items: IDeliveryItem[]): IDeliveryItem[] =>
    items.filter(item => !(item.fill && DELIVERY_EXCLUDED_MED_IDS.has(String(item?.fill?.prescribable?.medid))));

const hasValidDeliveryItems = (items: IDeliveryItem[]): boolean => {
    const hasFill = items.some(item => item.fill);
    const supplies = items.map(item => item.supply).filter(Boolean);
    return hasFill || supplies.some(item => item && DELIVERY_SUPPORTED_SUPPLY_TYPES.has(item.name));
};

// backend sends naive datetimes (no offset) which our dayjs wrapper treats as UTC, shifting the local day back
const stripTimeComponent = (date: string | null): string | null => (date ? date.split('T')[0] : date);

const normalizeDeliveryPackage = (pkg: IDeliveryPackage): IDeliveryPackage => ({ ...pkg, deliveredAt: stripTimeComponent(pkg.deliveredAt) });

const filterDeliveryPackages = (packages: IDeliveryPackage[]): IDeliveryPackage[] =>
    packages
        .map(pkg => ({ ...normalizeDeliveryPackage(pkg), deliveryItems: filterDeliveryItems(pkg.deliveryItems) }))
        .filter(pkg => hasValidDeliveryItems(pkg.deliveryItems));

const filterDeliveryTicket = (ticket: IDeliveryTicket): IDeliveryTicket => ({
    ...ticket,
    deliveryItems: filterDeliveryItems(ticket.deliveryItems),
    deliveryPackages: filterDeliveryPackages(ticket.deliveryPackages)
});



export interface IDashboardState {
    prescriptions: IStateDefaultField<ITransferPrescriptions[]>;
    pendingPrescriptions: IStateDefaultField<IPendingPrescription[]>;
    invoices: IStateDefaultField<IInvoice[]>;
    deliveryTickets: IStateDefaultField<IDeliveryTicket[]>;
    currentShipment: IConvertedShipment | IDeliveryTicket | null;
    requests: IStateDefaultField<ICustomerRequestItems>;
    addedRequest: IStateDefaultField<ICustomerRequestItem>;
    refillConsents: IStateDefaultField<IRefillConsent[]>;
}

export const initialState: IDashboardState = {
    prescriptions: getInitalStateDefField(),
    invoices: getInitalStateDefField(),
    deliveryTickets: getInitalStateDefField(),
    pendingPrescriptions: getInitalStateDefField(),
    currentShipment: null,
    requests: getInitalStateDefField(),
    addedRequest: getInitalStateDefField(),
    refillConsents: getInitalStateDefField()
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState: initialState,
    reducers: {
        setCurrentShipment: (state: IDashboardState, action: PayloadAction<IConvertedShipment | IDeliveryTicket | null>) => {
            state.currentShipment = action.payload;
        },
        updateDeliveryTicket: (state: IDashboardState, action: PayloadAction<IDeliveryTicket>) => {
            const updatedTicket = action.payload;
            const index = state.deliveryTickets.data?.findIndex(ticket => ticket.id === updatedTicket.id);
            if (index !== undefined && index >= 0) {
                state.deliveryTickets.data![index] = {
                    ...updatedTicket
                };

                if (state.currentShipment && state.currentShipment.id === updatedTicket.id) {
                    state.currentShipment = updatedTicket;
                }
            }
        },
        updateDeliveryPackage: (state: IDashboardState, action: PayloadAction<{ ticketId: number; deliveryPackage: IDeliveryPackage }>) => {
            const { ticketId, deliveryPackage } = action.payload;
            const ticketIndex = state.deliveryTickets.data?.findIndex(ticket => ticket.id === ticketId);
            if (ticketIndex === undefined || ticketIndex < 0) return;

            const ticket = state.deliveryTickets.data![ticketIndex];
            const packageIndex = ticket.deliveryPackages.findIndex(pkg => pkg.id === deliveryPackage.id);
            if (packageIndex < 0) return;

            const normalizedDeliveryPackage = normalizeDeliveryPackage(deliveryPackage);
            ticket.deliveryPackages[packageIndex] = { ...normalizedDeliveryPackage };

            if (state.currentShipment?.id === normalizedDeliveryPackage.id && 'address' in state.currentShipment) {
                state.currentShipment = { ...state.currentShipment, ...normalizedDeliveryPackage } as unknown as IConvertedShipment;
            }
        },
        updateInvoice: (state: IDashboardState, action: PayloadAction<IInvoice[]>) => {
            const updatedInvoices = action.payload;
            updatedInvoices.forEach(updatedInvoice => {
                const index = state.invoices.data?.findIndex(invoice => invoice.id === updatedInvoice.id);
                if (index !== undefined && index >= 0) {
                    state.invoices.data![index] = {
                        ...updatedInvoice,
                        payments: [...updatedInvoice.payments].sort((a, b) => a.id - b.id)
                    };
                }
            });
        },
        resetDashboardState: state => {
            Object.entries(initialState).forEach(([key, value]) => {
                state[key as keyof IDashboardState] = value;
            });
        }
    },
    extraReducers: builder => {
        handleApiCases(builder, 'prescriptions', CustomersController.getPrescriptions, {
            onFulfilled: (state, action) => {
                state.prescriptions.data = action.payload.filter(presc => {
                    return presc.stateTrigger !== StateTrigger.void && !NDCS_TO_HIDE.includes(presc?.ndc);
                });
            }
        });
        handleApiCases(builder, 'invoices', CustomersController.getInvoices, {
            onFulfilled: (state, action) => {
                state.invoices.data = action.payload
                    .map(invoice => ({
                        ...invoice,
                        invoiceLineItems: invoice.invoiceLineItems.filter(item => item.state !== 'DELETED'),
                        payments: [...invoice.payments].sort((a, b) => a.id - b.id)
                    }))
                    .filter(invoice => invoice.invoiceLineItems.length > 0 && isInvoiceValid(invoice.state));
            }
        });
        handleApiCases(builder, 'deliveryTickets', CustomersController.getDeliveryTickets, {
            onFulfilled: (state, action) => {
                state.deliveryTickets.data = action.payload
                    ?.map(filterDeliveryTicket)
                    .filter(ticket => hasValidDeliveryItems(ticket.deliveryItems));
            }
        });
        handleApiCases(builder, 'pendingPrescriptions', CustomersController.getPendingPrescriptions, {
            onFulfilled: (state, action) => {
                state.pendingPrescriptions.data = action.payload.filter(prescription => !prescription.handled);
            }
        });

        handleApiCases(builder, 'requests', CustomersController.getCustomerRequests, {
            onFulfilled: (state, action) => {
                const payloadOrEmptyArray = action.payload || [];
                state.requests.data = [...payloadOrEmptyArray].filter(request => request.state !== 'DONE');
            }
        });
        handleApiCases(builder, 'addedRequest', CustomersController.addCustomerRequest, {
            onFulfilled: (state, action) => {
                if (state.requests.data) {
                    state.requests.data.push(action.payload);
                }
            }
        });
        handleApiCases(builder, 'refillConsents', CustomersController.getRefillConsents);
    }
});

export const { setCurrentShipment, resetDashboardState, updateDeliveryTicket, updateDeliveryPackage, updateInvoice } = dashboardSlice.actions;

export default dashboardSlice;
